/**
 * Helper functins
 *  Map --> Helper to update or add a source + layer
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import "./TrafficMonitoringMap.css";
import config from "../../../../../config";
import { getEventIcon } from "modules/eventIcons";

mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN_3D;

import { Checkbox, FormControlLabel, Switch } from "@mui/material";
import dayjs from "dayjs";
import { width } from "@mui/system";

// Normalize category.name to be icon-safe
const normalizeIconName = (name) =>
  name?.toLowerCase().replace(/\s+/g, "-").trim();

const safeParse = (val) => {
  try {
    return typeof val === "string" ? JSON.parse(val) : val;
  } catch {
    return val;
  }
};

const getPopupCoordinates = (feature) => {
  if (!feature || !feature.geometry) return null;

  const { geometry } = feature;

  if (geometry.type === "Point" && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates.slice(); // defensive copy
  }

  if (
    geometry.type === "LineString" &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length > 0
  ) {
    const coordsArray = geometry.coordinates;
    const midIndex = Math.floor(coordsArray.length / 2);
    return coordsArray[midIndex];
  }

  return null;
};

const setColorLine = (limit, avgSpeed) => {
  const rgbColor = {
    green: "#12B51A",
    orange: "#E98500",
    red: "#C74811",
    darkRed: "#970029",
    // green: "#E98500",
    // orange: "#C74811",
    // red: "#970029",
    // darkRed: "#4C0035",
  };

  const thresholds = {
    130: { green: 91, orange: 52, red: 26 },
    90: { green: 63, orange: 36, red: 18 },
    85.4: { green: 58, orange: 34, red: 17 },
    84: { green: 58, orange: 34, red: 17 },
    70: { green: 49, orange: 28, red: 14 },
    60: { green: 42, orange: 24, red: 12 },
    50: { green: 36, orange: 21, red: 11 },
    40: { green: 28, orange: 16, red: 8 },
    36.8: { green: 24, orange: 14, red: 7 },
    35.9: { green: 24, orange: 14, red: 7 },
    35.8: { green: 24, orange: 14, red: 7 },
    36: { green: 24, orange: 14, red: 7 },
    29: { green: 20, orange: 12, red: 6 },
    20: { green: 14, orange: 8, red: 5 },
  };

  const t = thresholds[limit];

  if (!t) return rgbColor.darkRed; // default color if limit not found

  if (avgSpeed > t.green) {
    return rgbColor.green;
  } else if (avgSpeed > t.orange) {
    return rgbColor.orange;
  } else if (avgSpeed > t.red) {
    return rgbColor.red;
  } else {
    return rgbColor.darkRed;
  }
};

let areaRectHoverBound = false;
let areaRectHoverEnabled = false;

function formatMinuteDotSecond(value) {
  if (value == null || isNaN(value)) return "";

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const [minutePart, secondPart] = absValue.toString().split(".");
  const totalMinutes = parseInt(minutePart, 10);
  const seconds = secondPart ? parseInt(secondPart.padEnd(2, "0"), 10) : 0;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = isNegative ? "-" : "";
  if (hours > 0) result += `${hours} hr `;
  if (minutes > 0 || hours > 0) result += `${minutes} min `;
  result += `${seconds} sec`;

  return result.trim();
}

function TrafficMonitoringMap({
  onlyActive: events,
  networkSource,
  areaRectSource,
}) {
  const { t } = useTranslation();

  const map3D = config.MAP_3D;
  const map2D = "mapbox://styles/mapbox/dark-v11";

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const didMountRef = useRef(false);

  // State to manage checkboxes dynamically
  const [activeLayers, setActiveLayers] = useState({});
  const [dynamicLayers, setDynamicLayers] = useState([]);

  useEffect(() => {
    const layers = [];

    if (networkSource?.features?.length) {
      layers.push({ id: "network-layer", label: t("trafficMonitoring.monitoring.map.layers.network") });
    }

    if (events?.length) {
      layers.push({ id: "events-group", label: t("trafficMonitoring.monitoring.map.layers.events") });
    }

    if (areaRectSource?.features?.length) {
      layers.push({ id: "areaRect-layer", label: t("trafficMonitoring.monitoring.map.layers.areas") });
    }

    setDynamicLayers(layers);
  }, [networkSource, events, areaRectSource, t]);

  useEffect(() => {
    if (!dynamicLayers.length) return;

    const defaultState = dynamicLayers.reduce((acc, layer) => {
      acc[layer.id] = true; // Default all to visible
      return acc;
    }, {});
    setActiveLayers(defaultState);
  }, [dynamicLayers]);

  const handleLayerToggle = (layerId) => {
    if (!mapRef.current) return;

    if (layerId === "events-group") {
      const visibility =
        mapRef.current.getLayoutProperty("events-point-layer", "visibility") ===
        "visible"
          ? "none"
          : "visible";

      [
        "events-point-layer",
        "events-lineString-layer",
        "events-lineStringSymbol-layer",
      ].forEach((id) => {
        if (mapRef.current.getLayer(id)) {
          mapRef.current.setLayoutProperty(id, "visibility", visibility);
        }
      });

      setActiveLayers((prev) => ({
        ...prev,
        [layerId]: visibility === "visible",
      }));
    } else {
      const visibility =
        mapRef.current.getLayoutProperty(layerId, "visibility") === "visible"
          ? "none"
          : "visible";

      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.setLayoutProperty(layerId, "visibility", visibility);
      }

      //Add this: if toggling area fill, also toggle the outline
      if (
        layerId === "areaRect-layer" &&
        mapRef.current.getLayer("areaRect-layer-outline")
      ) {
        mapRef.current.setLayoutProperty(
          "areaRect-layer-outline",
          "visibility",
          visibility
        );
      }

      setActiveLayers((prev) => ({
        ...prev,
        [layerId]: visibility === "visible",
      }));
    }
  };

  const [selectedMapStyle, setSelectedMapStyle] = useState(map3D);

  // Displayed Message
  const displayHoverMessage = (feature) => {
    const props = feature.properties;
    const category = safeParse(props.category);
    const subcategory = safeParse(props.subcategory);
    const source = safeParse(props.source);
    const type_of_event = safeParse(props.type_of_event);

    return `
      <div>
      ${category?.name ? `${t("trafficMonitoring.monitoring.map.popup.category")} ` : ""}
         ${category?.name || ""}${
      subcategory?.name ? ` (${subcategory.name})` : ""
    }<br />
        ${type_of_event?.name ? `${t("trafficMonitoring.monitoring.map.popup.type")} ${type_of_event.name}` : ""}<br/>
        ${source?.name ? `${t("trafficMonitoring.monitoring.map.popup.source")} (${source.name})` : ""}<br/>
      </div>
    `;
  };

  const displayClickMessage = (feature) => {
    const props = feature.properties;
    const category = safeParse(props.category);
    const subcategory = safeParse(props.subcategory);
    const source = safeParse(props.source);
    const type_of_event = safeParse(props.type_of_event);

    return `
      <div class="custom-popup-content">
      ${category?.name ? `${t("trafficMonitoring.monitoring.map.popup.category")} ` : ""}
         ${category?.name || ""}${
      subcategory?.name ? ` (${subcategory.name})` : ""
    }<br />
        ${type_of_event?.name ? `${t("trafficMonitoring.monitoring.map.popup.type")} ${type_of_event.name}` : ""}<br/>
        ${source?.name ? `${t("trafficMonitoring.monitoring.map.popup.source")} (${source.name})` : ""}<br/>
    </div>`;
  };

  // set and reset Features
  const resetFeatureState = (map, source, id, stateKey) => {
    if (id !== null) {
      map.setFeatureState({ source, id }, { [stateKey]: false });
    }
  };

  const setFeatureState = (map, source, id, stateKey) => {
    map.setFeatureState({ source, id }, { [stateKey]: true });
  };

  const renderNetworkLayer = (map) => {
    if (!map || !networkSource?.features?.length) return;

    // 1. Build FeatureCollection with top-level string IDs
    const networkFeatures = networkSource.features.map((f) => ({
      type: "Feature",
      id: f.properties.id_path, // ✅ ID required for feature-state
      geometry: f.geometry,
      properties: {
        ...f.properties,
        colorLine: setColorLine(
          f.properties.speed_limit,
          f.properties.avg_speed_paths
        ),
      },
    }));

    // Helper to update or add a source + layer
    const updateOrAdd = (sourceId, layerId, layerConfig, data) => {
      const existingSource = map.getSource(sourceId);

      if (existingSource) {
        existingSource.setData(data);
      } else {
        map.addSource(sourceId, {
          type: "geojson",
          data,
        });

        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            source: sourceId,
            ...layerConfig,
          });
        }
      }
    };

    // Add source & layer for linestring [network-source, network-layer]
    updateOrAdd(
      "network-source",
      "network-layer",
      {
        type: "line",
        slot: "middle",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-emissive-strength": 1.0,
          "line-color": ["get", "colorLine"],
          "line-width": [
            "case",
            // 1) if colorLine is exactly "#fff", use width 20
            ["==", ["get", "colorLine"], "#970029"],
            8,
            ["boolean", ["feature-state", "click"], false],
            8,
            4,
          ],
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "click"], false],
            0.4,
            ["boolean", ["feature-state", "hover"], false],
            0.5,
            1,
          ],
        },
      },
      {
        type: "FeatureCollection",
        features: networkFeatures,
      }
    );

    if (map.__networkInteractivityBound) return;
    map.__networkInteractivityBound = true;

    // 5. Hover Interactivity
    const hoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });

    let hoveredNetworkId = null;

    map.on("mouseenter", "network-layer", (e) => {
      map.getCanvas().style.cursor = "pointer";
      const feature = e.features[0];
      if (!feature) return;

      const hoveredId = feature.id;
      const props = feature.properties;
      const delay = formatMinuteDotSecond(props.delay);
      const tt_paths = formatMinuteDotSecond(props.tt_paths);
      const date = dayjs(props.timestamp).format("DD/MM/YYYY HH:mm");

      resetFeatureState(map, "network-source", hoveredNetworkId, "hover");
      hoveredNetworkId = hoveredId;
      setFeatureState(map, "network-source", hoveredNetworkId, "hover");

      const content = `
        <div>
          <p><strong>Average Speed:</strong> ${props.avg_speed_paths} km/h</p>
          <p><strong>Delay:</strong> ${delay}</p>
          <p><strong>Travel Time:</strong> ${tt_paths}</p>
          <p><strong>Time:</strong> ${date}</p>
        </div>
      `;

      hoverPopup.setLngLat(e.lngLat).setHTML(content).addTo(map);
    });

    map.on("mouseleave", "network-layer", () => {
      map.getCanvas().style.cursor = "";
      hoverPopup.remove();

      if (hoveredNetworkId !== null) {
        resetFeatureState(map, "network-source", hoveredNetworkId, "hover");
        hoveredNetworkId = null;
      }
    });

    // 6. Click Interactivity
    const clickPopup = new mapboxgl.Popup({
      className: "my-custom-popup",
      closeButton: true,
      closeOnClick: true,
    });

    let clickedNetworkId = null;

    map.on("click", "network-layer", (e) => {
      const feature = e.features[0];
      if (!feature) return;

      const clickedId = feature.id;
      const props = feature.properties;
      const delay = formatMinuteDotSecond(props.delay);
      const tt_paths = formatMinuteDotSecond(props.tt_paths);
      const date = dayjs(props.timestamp).format("DD/MM/YYYY HH:mm");

      resetFeatureState(map, "network-source", clickedNetworkId, "click");
      clickedNetworkId = clickedId;
      setFeatureState(map, "network-source", clickedNetworkId, "click");

      const content = `
        <div class="custom-popup-content">
          <p><strong>Average Speed:</strong> ${props.avg_speed_paths} km/h</p>
          <p><strong>Delay:</strong> ${delay}</p>
          <p><strong>Travel Time:</strong> ${tt_paths}</p>
          <p><strong>Time:</strong> ${date}</p>
        </div>
      `;

      clickPopup.setLngLat(e.lngLat).setHTML(content).addTo(map);

      // Reset style when popup closes
      clickPopup.on("close", () => {
        if (clickedNetworkId !== null) {
          resetFeatureState(map, "network-source", clickedNetworkId, "click");
          clickedNetworkId = null;
        }
      });
    });
  };

  // Helper to render layers
  // Helper to render layers
  // Helper to render layers
  const renderEventLayers = (map) => {
    if (!map || !events?.length) return;

    // 2. Organize features
    const pointFeatures = [];
    const lineFeatures = [];
    const lineSymbolFeatures = [];
    const uniqueIcons = new Map();

    // 3. Create feature to pass on source based on geom
    events.forEach((event) => {
      const geom = event.geometry;
      if (!geom) return;

      const iconName = normalizeIconName(event.category?.name);
      const iconPath = getEventIcon(event.category?.id);

      // Exclude nested geometry from properties
      const { geometry, ...eventWithoutGeometry } = event;

      const feature = {
        id: event.id,
        type: "Feature",
        geometry: {
          type: geom.type,
          coordinates:
            geom.type === "Point" ? geom.coordinates[0] : geom.coordinates,
        },
        properties: {
          ...eventWithoutGeometry,
          icon: iconName,
        },
      };

      if (geom.type === "Point") {
        pointFeatures.push(feature);
        if (!uniqueIcons.has(iconName)) {
          uniqueIcons.set(iconName, iconPath);
        }
      } else if (geom.type === "LineString") {
        lineFeatures.push(feature);
        if (!uniqueIcons.has(iconName)) {
          uniqueIcons.set(iconName, iconPath);
        }

        // Create a symbol at the first coordinate of the line
        lineSymbolFeatures.push({
          id: `${event.id}`,
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: geom.coordinates[0],
          },
          properties: {
            ...eventWithoutGeometry,
            icon: iconName,
            isLine: true,
          },
        });
      }
    });

    // 4. Load unique icons
    uniqueIcons.forEach((path, name) => {
      if (!map.hasImage(name)) {
        map.loadImage(path, (err, image) => {
          if (err) return console.error("Error loading icon:", err);
          if (!map.hasImage(name)) {
            map.addImage(name, image);
          }
        });
      }
    });

    // Helper to update or add a source + layer
    const updateOrAdd = (sourceId, layerId, layerConfig, data) => {
      const existingSource = map.getSource(sourceId);

      if (existingSource) {
        existingSource.setData(data);
      } else {
        map.addSource(sourceId, {
          type: "geojson",
          data,
        });

        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            source: sourceId,
            ...layerConfig,
          });
        }
      }
    };

    // 5a. Add source & layer for points [events-point, events-point-layer]
    updateOrAdd(
      "events-point",
      "events-point-layer",
      {
        type: "symbol",
        slot: "top",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 0.9,
        },
        paint: {
          "icon-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            1.5,
            1,
          ],
        },
      },
      {
        type: "FeatureCollection",
        features: pointFeatures,
      }
    );

    // 5b. Add source & layer for linestring [events-lineString, events-lineString-layer]
    updateOrAdd(
      "events-lineString",
      "events-lineString-layer",
      {
        type: "line",
        slot: "top",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-emissive-strength": 1.0,
          "line-color": "#b33b32",
          "line-width": 1,
          "line-opacity": 0.5,
        },
      },
      {
        type: "FeatureCollection",
        features: lineFeatures,
      }
    );

    // 5c. Add source & layer for linestring-symbol [events-lineStringSymbol, events-lineStringSymbol-layer]
    updateOrAdd(
      "events-lineStringSymbol",
      "events-lineStringSymbol-layer",
      {
        type: "symbol",
        slot: "top",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 0.9,
        },
      },
      {
        type: "FeatureCollection",
        features: lineSymbolFeatures,
      }
    );

    if (map.__eventsInteractivityBound) return;
    map.__eventsInteractivityBound = true;

    // 6. Add popup on hover for points and lines
    const hoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });

    const hoverLayers = ["events-point-layer", "events-lineStringSymbol-layer"];

    let hoveredPointId = null;
    let hoveredLineId = null;

    // 7. Hover handlers
    hoverLayers.forEach((layerId) => {
      map.on("mouseenter", layerId, (e) => {
        map.getCanvas().style.cursor = "pointer";

        const feature = e.features[0];
        if (!feature) return;

        const coordinates = getPopupCoordinates(feature);
        if (!coordinates) return;

        const content = displayHoverMessage(feature);

        if (layerId === "events-point-layer") {
          resetFeatureState(map, "events-point", hoveredPointId, "hover");
          hoveredPointId = feature.id;
          setFeatureState(map, "events-point", hoveredPointId, "hover");
        }

        if (layerId === "events-lineStringSymbol-layer") {
          resetFeatureState(
            map,
            "events-lineStringSymbol",
            hoveredLineId,
            "hover"
          );
          hoveredLineId = feature.id;
          setFeatureState(
            map,
            "events-lineStringSymbol",
            hoveredLineId,
            "hover"
          );
        }

        hoverPopup.setLngLat(coordinates).setHTML(content).addTo(map);
      });

      map.on("mouseleave", layerId, () => {
        map.getCanvas().style.cursor = "";
        hoverPopup.remove();

        if (layerId === "events-point-layer" && hoveredPointId !== null) {
          resetFeatureState(map, "events-point", hoveredPointId, "hover");
          hoveredPointId = null;
        }

        if (
          layerId === "events-lineStringSymbol-layer" &&
          hoveredLineId !== null
        ) {
          resetFeatureState(
            map,
            "events-lineStringSymbol",
            hoveredLineId,
            "hover"
          );
          hoveredLineId = null;
        }
      });
    });

    // 8. Click popup
    const clickLayers = ["events-point-layer", "events-lineStringSymbol-layer"];

    let clickedPointId = null;
    let clickedLineId = null;

    clickLayers.forEach((layerId) => {
      map.on("click", layerId, (e) => {
        // 👇 IGNORE CLICKS COMING FROM THE POPUP X BUTTON
        const target = e.originalEvent?.target;
        if (
          target &&
          target.classList &&
          target.classList.contains("mapboxgl-popup-close-button")
        ) {
          return;
        }

        const feature = e.features[0];
        if (!feature || !feature.id) return;

        const coordinates = getPopupCoordinates(feature);
        if (!coordinates) return;

        const content = displayClickMessage(feature);

        // Reset previous clicked states
        if (clickedPointId !== null && layerId === "events-point-layer") {
          resetFeatureState(map, "events-point", clickedPointId, "click");
        }

        if (
          clickedLineId !== null &&
          layerId === "events-lineStringSymbol-layer"
        ) {
          resetFeatureState(
            map,
            "events-lineStringSymbol",
            clickedLineId,
            "click"
          );
        }

        // Set new clicked states
        if (layerId === "events-point-layer") {
          clickedPointId = feature.id;
          setFeatureState(map, "events-point", clickedPointId, "click");
        }

        if (layerId === "events-lineStringSymbol-layer") {
          clickedLineId = feature.id;
          setFeatureState(
            map,
            "events-lineStringSymbol",
            clickedLineId,
            "click"
          );
        }

        // Create popup for this click
        const popup = new mapboxgl.Popup({
          className: "my-custom-popup",
          closeButton: true,
          closeOnClick: true,
        });

        popup.setLngLat(coordinates).setHTML(content).addTo(map);

        // Reset style when popup closes
        popup.on("close", () => {
          if (clickedPointId !== null) {
            resetFeatureState(map, "events-point", clickedPointId, "click");
            clickedPointId = null;
          }

          if (clickedLineId !== null) {
            resetFeatureState(
              map,
              "events-lineStringSymbol",
              clickedLineId,
              "click"
            );
            clickedLineId = null;
          }
        });
      });
    });
  };

  // Helper to render areaRect
  const renderAreaRectLayer = (map) => {
    if (!areaRectSource?.features?.length) return;

    const geojsonData = {
      type: "FeatureCollection",
      features: areaRectSource.features.filter(
        (f) => f.properties.area_id !== 100
      ),
    };

    areaRectHoverEnabled = geojsonData.features.length > 1;

    // Source
    if (!map.getSource("areaRect-source")) {
      map.addSource("areaRect-source", {
        type: "geojson",
        data: geojsonData,
        promoteId: "area_id",
      });
    } else {
      map.getSource("areaRect-source").setData(geojsonData);
    }

    // Layer
    if (!map.getLayer("areaRect-layer")) {
      map.addLayer({
        id: "areaRect-layer",
        type: "fill",
        source: "areaRect-source",
        paint: {
          "fill-emissive-strength": 1.0,
          "fill-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            "#545454", // hover:
            "#666666", // default
          ],
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.7, // λίγο πιο έντονο
            0.5,
          ],
        },
      });
    }

    if (!map.getLayer("areaRect-layer-outline")) {
      map.addLayer({
        id: "areaRect-layer-outline",
        type: "line",
        source: "areaRect-source",
        paint: {
          "line-emissive-strength": 1.0,
          "line-color": "#000",
          "line-width": 2,
        },
      });
    }
    // onHover("areaRect-layer");

    if (!areaRectHoverBound) {
      areaRectHoverBound = true;

      const hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });

      let hoveredAreaId = null;

      map.on("mouseenter", "areaRect-layer", (e) => {
        if (!areaRectHoverEnabled) return;

        map.getCanvas().style.cursor = "pointer";

        const feature = e.features && e.features[0];
        if (!feature) return;

        const props = feature.properties || {};
        const label = props.name || "";

        const id = feature.id;
        if (id == null) return;

        // reset previous hover
        if (hoveredAreaId !== null) {
          map.setFeatureState(
            { source: "areaRect-source", id: hoveredAreaId },
            { hover: false }
          );
        }
        hoveredAreaId = id;

        // set current hover
        map.setFeatureState(
          { source: "areaRect-source", id: hoveredAreaId },
          { hover: true }
        );

        hoverPopup
          .setLngLat(e.lngLat)
          .setHTML(`<div><strong>${label}</strong></div>`)
          .addTo(map);
      });

      map.on("mousemove", "areaRect-layer", (e) => {
        if (!areaRectHoverEnabled) return;
        if (!hoverPopup.isOpen()) return;
        hoverPopup.setLngLat(e.lngLat);
      });

      map.on("mouseleave", "areaRect-layer", () => {
        if (!areaRectHoverEnabled) return;
        map.getCanvas().style.cursor = "";
        hoverPopup.remove();

        // clear the hover highlight
        if (hoveredAreaId !== null) {
          map.setFeatureState(
            { source: "areaRect-source", id: hoveredAreaId },
            { hover: false }
          );
          hoveredAreaId = null;
        }
      });
    }
  };

  // 1. Initialize the map
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      // style: "mapbox://styles/mapbox/dark-v11",
      style: selectedMapStyle,
      projection: "mercator", // ← this makes transform.projection defined
      center: [22.9527, 40.6253],
      zoom: 11,
    });

    mapRef.current = map;
    map.once("load", () => {
      renderNetworkLayer(map);
      renderEventLayers(map);
      renderAreaRectLayer(map);
    });

    // Cleanup on unmount
    return () => {
      // reset global flags κάθε φορά που φεύγουμε από το component/tab
      areaRectHoverBound = false;
      areaRectHoverEnabled = false;
      map.remove();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const doUpdate = () => {
      renderNetworkLayer(map);
      renderEventLayers(map);
      renderAreaRectLayer(map);
    };

    if (map.isStyleLoaded()) {
      // style is ready: update immediately
      doUpdate();
    } else {
      map.once("load", doUpdate);
    }
  }, [events, networkSource, areaRectSource]);

  // 3️⃣ REBUILD ON STYLE SWITCH
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    map.setStyle(selectedMapStyle);

    // after new style + tiles load, re-draw and restore visibilities
    map.once("styledata", (e) => {
      if (e.dataType !== "style") return;

      renderNetworkLayer(map);
      renderEventLayers(map);
      renderAreaRectLayer(map);
      Object.entries(activeLayers).forEach(([layerGroup, visible]) => {
        const vis = visible ? "visible" : "none";
        let ids;
        if (layerGroup === "events-group") {
          ids = [
            "events-point-layer",
            "events-lineString-layer",
            "events-lineStringSymbol-layer",
          ];
        } else if (layerGroup === "areaRect-layer") {
          ids = ["areaRect-layer", "areaRect-layer-outline"];
        } else {
          ids = [layerGroup];
        }
        ids.forEach((lid) => {
          if (map.getLayer(lid)) {
            map.setLayoutProperty(lid, "visibility", vis);
          }
        });
      });
    });
  }, [selectedMapStyle]);

  //  VISIBILITY-TOGGLE EFFECT
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) {
      return;
    }
    Object.entries(activeLayers).forEach(([layerGroup, visible]) => {
      const vis = visible ? "visible" : "none";
      let ids;
      if (layerGroup === "events-group") {
        ids = [
          "events-point-layer",
          "events-lineString-layer",
          "events-lineStringSymbol-layer",
        ];
      } else if (layerGroup === "areaRect-layer") {
        ids = ["areaRect-layer", "areaRect-layer-outline"];
      } else {
        ids = [layerGroup];
      }
      ids.forEach((lid) => {
        if (map.getLayer(lid)) {
          map.setLayoutProperty(lid, "visibility", vis);
        }
      });
    });
  }, [activeLayers]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          background: "#202940",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "8px",
        }}
      >
        {/* Checkboxes to Toggle Layers */}
        {dynamicLayers.map((layer) => (
          <FormControlLabel
            key={layer.id}
            control={
              <Checkbox
                checked={!!activeLayers[layer.id]}
                onChange={() => handleLayerToggle(layer.id)} 
              />
            }
            label={layer.label}
          />
        ))}
        {/* Toggle Switch */}
        <FormControlLabel
          control={
            <Switch
              className="custom-switch-3D"
              checked={selectedMapStyle === map3D}
              onChange={() =>
                setSelectedMapStyle((prev) => (prev === map3D ? map2D : map3D))
              }
            />
          }
          label={selectedMapStyle === map3D ? t("trafficMonitoring.monitoring.map.threeDOn") : t("trafficMonitoring.monitoring.map.threeDOff")}
        />
      </div>

      <div
        id="trafficMonitoring-map"
        ref={mapContainerRef}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

export default TrafficMonitoringMap;
